import { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Table,
  Badge,
} from "react-bootstrap";
import Select from "react-select";
import moment from "moment";
import { toast } from "react-toastify";
import "./TicketEditModal.css";
import {
  updateTicket,
  closeTicket,
  addTicketWorkLog,
  getAllTicketClosureTypes,
  getAllTicketLoggedTypes,
  getAllTicketStatuses,
  getTicketWorkDetails,
  getContactNames,
} from "../Api/TicketApiOrg";
import { ticketDropdownApi } from "../Api/TicketApiOrg";
import { fetchResourceById } from "../Api/ResourceMaster";
import { getResourceByMavUserId } from "../Api/ResourceList";
import { fetchCusVenCpById } from "../Api/CusVenCpMasterApi";
import { IoMdRefreshCircle } from "react-icons/io";

const TicketEditModal = ({
  show,
  onClose,
  ticket,
  orgId,
  userDetails,
  projectOptions,
  fetchProjectAndPartners,
  refreshTickets,
  currentProject,
  isProjectReadOnly = false,
  isLoading = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [showWorkLogModal, setShowWorkLogModal] = useState(false);
  const [ticketReasons, setTicketReasons] = useState([]);
  const [closureTypes, setClosureTypes] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // State for work logs
  const [workLogsData, setWorkLogsData] = useState(null);
  const [loadingWorkLogs, setLoadingWorkLogs] = useState(false);

  // State for user names cache
  const [userNamesCache, setUserNamesCache] = useState({});
  const [loadingUserNames, setLoadingUserNames] = useState(false);

  const [submittedByOptions, setSubmittedByOptions] = useState([]);
  const [loadingSubmittedBy, setLoadingSubmittedBy] = useState(false);

  // Projects by Submitter
  const [projectOptionsBySubmitter, setProjectOptionsBySubmitter] = useState(
    [],
  );
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Status Master Data
  const [statusOptions, setStatusOptions] = useState([]);
  const [subStatusMap, setSubStatusMap] = useState({});
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  // ========== SEPARATE LISTS FOR DIFFERENT PURPOSES ==========

  // 1. For "Assigned To" field - includes Team, Customer, Partner (with labels)
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [loadingAssignedTo, setLoadingAssignedTo] = useState(false);

  // 2. For "Submitted By" field - uses separate API
  // Already using submittedByOptions state above

  // 3. For "Work Log Worked By" field - NOW SAME AS ASSIGNED TO (Team, Customer, Partner)
  const [workLogUserOptions, setWorkLogUserOptions] = useState([]);
  const [loadingWorkLogUsers, setLoadingWorkLogUsers] = useState(false);

  const [isClosureMode, setIsClosureMode] = useState(false);
  const [workLogUserOptionsCache, setWorkLogUserOptionsCache] = useState({});

  const [typeOptions, setTypeOptions] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [updateForm, setUpdateForm] = useState({
    ticketName: "",
    description: "",
    status: "",
    priority: "",
    assignedTo: [],
    submittedBy: "",
    category: "",
    dueDate: "",
    referenceTo: "",
    projectAssigned: "",
    ticketType: "",
    ticketReason: "",
    subStatus: "",
  });

  // Closure form state (inline)
  const [closureForm, setClosureForm] = useState({
    closureTypeCode: "",
    closureRemarks: "",
    efforts: [
      {
        workedBy: "",
        effortValue: "",
        effortUnit: "HOURS",
      },
    ],
  });
  const [isClosing, setIsClosing] = useState(false);

  const [workLogForm, setWorkLogForm] = useState({
    workedBy: "",
    effortValue: "",
    effortUnit: "HOURS",
    remarks: "",
  });

  // Helper to check if a string is a name (contains spaces and letters)
  const isNameString = (str) => {
    if (!str) return false;
    return (
      /^[A-Za-z\s]+$/.test(str) && !str.startsWith("CVCP") && isNaN(Number(str))
    );
  };

  // ========== FUNCTIONS FOR WORK LOG USERS (SAME AS ASSIGNED TO) ==========

  // Build work log user options (SAME AS ASSIGNED TO - Team, Customer, Partner)
  // Build work log user options (SAME AS ASSIGNED TO - Team, Customer, Partner)
  const buildWorkLogUserOptions = async (forceRefresh = false) => {
    if (!updateForm.projectAssigned || !orgId) {
      setWorkLogUserOptions([]);
      return;
    }

    setLoadingWorkLogUsers(true);

    try {
      const projectIds = updateForm.projectAssigned
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id); // Remove empty strings

      if (projectIds.length === 0) {
        setWorkLogUserOptions([]);
        return;
      }

      // Create a cache key based on sorted project IDs
      const cacheKey = projectIds.sort().join(",");

      // Check cache if not forcing refresh
      if (!forceRefresh && workLogUserOptionsCache[cacheKey]) {
        console.log("Using cached work log options for:", cacheKey);
        setWorkLogUserOptions(workLogUserOptionsCache[cacheKey]);
        setLoadingWorkLogUsers(false);
        return;
      }

      const userMap = new Map();

      for (const pid of projectIds) {
        try {
          const [team, customers, partners] = await Promise.all([
            ticketDropdownApi.getTeamAssigned(orgId, pid),
            ticketDropdownApi.getCustomer(orgId, pid),
            ticketDropdownApi.getPartner(orgId, pid),
          ]);

          // TEAM
          team.forEach((item) => {
            if (item.contactGUID && !userMap.has(item.contactGUID)) {
              userMap.set(item.contactGUID, {
                value: item.contactGUID,
                label: `${item.contactName} (Team)`,
                type: "Team",
              });
            }
          });

          // CUSTOMER
          customers.forEach((item) => {
            if (item.contactGUID && !userMap.has(item.contactGUID)) {
              userMap.set(item.contactGUID, {
                value: item.contactGUID,
                label: `${item.contactName} (Customer)`,
                type: "Customer",
              });
            }
          });

          // PARTNER
          partners.forEach((item) => {
            if (item.contactGUID && !userMap.has(item.contactGUID)) {
              userMap.set(item.contactGUID, {
                value: item.contactGUID,
                label: `${item.contactName} (Partner)`,
                type: "Partner",
              });
            }
          });
        } catch (error) {
          console.error(`Failed to load users for project ${pid}:`, error);
          // Continue with other projects even if one fails
        }
      }

      const sortedUsers = Array.from(userMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );

      // Cache the results
      setWorkLogUserOptionsCache((prev) => ({
        ...prev,
        [cacheKey]: sortedUsers,
      }));

      setWorkLogUserOptions(sortedUsers);

      if (sortedUsers.length === 0 && isClosureMode) {
        toast.warning(
          "No team members found for the selected project(s). Please add team members to the project first.",
        );
      }
    } catch (error) {
      console.error("Failed to build work log user options:", error);
      toast.error("Failed to load work log user options");
      setWorkLogUserOptions([]);
    } finally {
      setLoadingWorkLogUsers(false);
    }
  };

  // ========== FUNCTIONS FOR ASSIGNED TO (Team, Customer, Partner) ==========

  // Build assigned to options with specific project IDs
  const buildAssignedToOptionsWithProjects = async (projectIds) => {
    if (!projectIds || projectIds.length === 0) {
      setAssignedToOptions([]);
      return;
    }

    setLoadingAssignedTo(true);

    try {
      const assigneeMap = new Map();

      for (const pid of projectIds) {
        const [team, customers, partners] = await Promise.all([
          ticketDropdownApi.getTeamAssigned(orgId, pid),
          ticketDropdownApi.getCustomer(orgId, pid),
          ticketDropdownApi.getPartner(orgId, pid),
        ]);

        // TEAM
        team.forEach((item) => {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Team)`,
          });
        });

        // CUSTOMER
        customers.forEach((item) => {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Customer)`,
          });
        });

        // PARTNER
        partners.forEach((item) => {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Partner)`,
          });
        });
      }

      const finalList = Array.from(assigneeMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );

      setAssignedToOptions(finalList);
    } catch (error) {
      console.error("Failed to load assigned to options:", error);
      toast.error("Failed to load assignee options");
    } finally {
      setLoadingAssignedTo(false);
    }
  };

  // Build assigned to options based on current projectAssigned
  const buildAssignedToOptions = async () => {
    if (!updateForm.projectAssigned) {
      setAssignedToOptions([]);
      return;
    }

    const projectIds = updateForm.projectAssigned
      .split(",")
      .map((id) => id.trim());
    await buildAssignedToOptionsWithProjects(projectIds);
  };

  // ========== SUBMITTED BY (Separate API) ==========

  const loadSubmittedBy = async () => {
    if (!orgId) return;

    setLoadingSubmittedBy(true);
    try {
      const data = await ticketDropdownApi.getSubmittedBy(orgId);

      const uniqueMap = new Map();

      data.forEach((item) => {
        if (!uniqueMap.has(item.contactGuid)) {
          uniqueMap.set(item.contactGuid, {
            value: item.contactGuid,
            label: item.description,
          });
        }
      });

      const options = Array.from(uniqueMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );

      setSubmittedByOptions(options);
    } catch (error) {
      console.error("Failed to load Submitted By:", error);
    } finally {
      setLoadingSubmittedBy(false);
    }
  };

  // ========== PROJECTS BY SUBMITTED BY ==========

  const fetchProjectsBySubmittedBy = async (submittedByValue) => {
    if (!orgId || !submittedByValue) {
      setProjectOptionsBySubmitter([]);
      return;
    }

    setLoadingProjects(true);
    try {
      const projects = await ticketDropdownApi.getProjectsBySubmittedBy(
        orgId,
        submittedByValue,
      );

      // Format projects for Select component
      const formattedProjects = projects.map((project) => ({
        value: project.project_id,
        label: `${project.pr_Title} (${project.project_id})`,
      }));

      setProjectOptionsBySubmitter(formattedProjects);

      // If current projectAssigned is not in the new list, clear it
      if (updateForm.projectAssigned) {
        const currentProjectIds = updateForm.projectAssigned.split(",");
        const validProjects = currentProjectIds.filter((id) =>
          formattedProjects.some((p) => p.value === id),
        );

        if (validProjects.length !== currentProjectIds.length) {
          setUpdateForm((prev) => ({
            ...prev,
            projectAssigned: validProjects.join(","),
            assignedTo: [], // Clear assignedTo when project changes
          }));

          // Reload assignedTo options with new projects
          if (validProjects.length > 0) {
            await buildAssignedToOptionsWithProjects(validProjects);
            // Also reload work log user options
            await buildWorkLogUserOptions();
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch projects by submittedBy:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  const getProjectCountText = () => {
    const selectedProjects =
      updateForm.projectAssigned?.split(",").filter((p) => p) || [];
    const totalProjects = projectOptionsBySubmitter.length;

    if (selectedProjects.length === 0) {
      return `0/${totalProjects} projects selected`;
    }
    return `${selectedProjects.length}/${totalProjects} projects selected`;
  };

  // ========== USER NAME RESOLUTION FOR DISPLAY USING getContactNames API ==========

  // Fetch user names in batch using getContactNames API
  const fetchUserNamesInBatch = async (userIds) => {
    if (!userIds || userIds.length === 0) return {};

    console.log("=== FETCHING USER NAMES ===");
    console.log("User IDs to fetch:", userIds);

    try {
      const uncachedIds = userIds.filter((id) => !userNamesCache[id]);
      console.log("Uncached IDs:", uncachedIds);

      if (uncachedIds.length === 0) {
        console.log("All IDs already in cache:", userNamesCache);
        return userNamesCache;
      }

      // ✅ Use the standalone getContactNames function (POST version)
      console.log("Calling getContactNames API with IDs:", uncachedIds);
      const contacts = await getContactNames(uncachedIds); // Changed from ticketDropdownApi.getContactNames
      console.log("API Response from getContactNames:", contacts);

      const nameMap = {};
      contacts.forEach((contact) => {
        console.log(
          `Mapping: ${contact.contactGuid} -> ${contact.contactName}`,
        );
        if (contact.contactGuid && contact.contactName) {
          nameMap[contact.contactGuid] = contact.contactName;
        }
      });

      console.log("Created nameMap:", nameMap);

      setUserNamesCache((prev) => {
        const newCache = {
          ...prev,
          ...nameMap,
        };
        console.log("Updated userNamesCache:", newCache);
        return newCache;
      });

      return nameMap;
    } catch (error) {
      console.error("Failed to fetch user names in batch:", error);
      return {};
    }
  };

  // Resolve work log user names using batch API
  const resolveWorkLogUserNames = async (workLogsByUser) => {
    if (!workLogsByUser || workLogsByUser.length === 0) return;

    const uniqueUserIds = [
      ...new Set(
        workLogsByUser.map((userWork) => userWork.userId).filter(Boolean),
      ),
    ];

    if (uniqueUserIds.length === 0) return;

    setLoadingUserNames(true);
    try {
      await fetchUserNamesInBatch(uniqueUserIds);
    } catch (error) {
      console.error("Error resolving user names:", error);
    } finally {
      setLoadingUserNames(false);
    }
  };

  // Load work logs data from API
  // In loadWorkLogs function - add this console
  const loadWorkLogs = async () => {
    if (!ticket?.ticketId) return;

    setLoadingWorkLogs(true);
    try {
      const data = await getTicketWorkDetails(ticket.ticketId);
      console.log("=== WORK LOGS DATA RECEIVED ===");
      console.log("Full work logs data:", data);
      console.log("workLogsByUser array:", data?.workLogsByUser);

      if (data?.workLogsByUser) {
        data.workLogsByUser.forEach((userWork, idx) => {
          console.log(`User ${idx + 1}:`, {
            userId: userWork.userId,
            logCount: userWork.logCount,
            logs: userWork.logs,
          });
        });
      }

      setWorkLogsData(data);

      if (data?.workLogsByUser && data.workLogsByUser.length > 0) {
        await resolveWorkLogUserNames(data.workLogsByUser);
      }
    } catch (error) {
      console.error("Failed to load work logs:", error);
    } finally {
      setLoadingWorkLogs(false);
    }
  };

  // Load statuses from API
  const loadTicketStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const data = await getAllTicketStatuses();

      const mainStatuses = data.map((status) => ({
        value: status.statusGuid,
        label: status.statusName,
        statusId: status.statusId,
        requiresClosure: status.requiresClosure,
        isTerminal: status.isTerminal,
      }));

      setStatusOptions(mainStatuses);

      const subMap = {};
      data.forEach((mainStatus) => {
        if (mainStatus.subStatuses && mainStatus.subStatuses.length > 0) {
          subMap[mainStatus.statusGuid] = mainStatus.subStatuses.map((sub) => ({
            value: sub.statusGuid,
            label: sub.statusName,
            isDefault: sub.isDefault,
            requiresClosure: sub.requiresClosure,
            isTerminal: sub.isTerminal,
          }));
        } else {
          subMap[mainStatus.statusGuid] = [];
        }
      });

      setSubStatusMap(subMap);
    } catch (error) {
      console.error("Failed to load statuses:", error);
      toast.error("Failed to load status options");
    } finally {
      setLoadingStatuses(false);
    }
  };

  // ========== REFRESH FUNCTION ==========

  const refreshAllData = async () => {
    setRefreshing(true);
    try {
      // Clear cache
      setUserNamesCache({});

      // Reload work logs
      await loadWorkLogs();

      // Reload projects if submittedBy exists
      if (updateForm.submittedBy) {
        await fetchProjectsBySubmittedBy(updateForm.submittedBy);
      }

      // Reload assignedTo and workLogUser options based on current projects
      if (updateForm.projectAssigned) {
        const projectIds = updateForm.projectAssigned
          .split(",")
          .map((id) => id.trim());
        await buildAssignedToOptionsWithProjects(projectIds);
        await buildWorkLogUserOptions();
      }

      toast.success("Data refreshed successfully");
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Failed to refresh data");
    } finally {
      setRefreshing(false);
    }
  };

  // ========== TYPE, REASON, CATEGORY HANDLERS ==========

  const handleTypeChange = async (selected) => {
    const typeGuid = selected?.value || "";

    setUpdateForm({
      ...updateForm,
      ticketType: typeGuid,
      ticketReason: "",
      category: "",
    });

    setReasonOptions([]);
    setCategoryOptions([]);

    if (!typeGuid) return;

    try {
      const reasons = await ticketDropdownApi.getTicketLoggedType(
        orgId,
        typeGuid,
      );

      const formattedReasons = reasons.map((r) => ({
        value: r.ticket_log_guid,
        label: r.ticketdescription,
      }));

      setReasonOptions(formattedReasons);
    } catch (error) {
      console.error("Failed to load reasons:", error);
    }
  };

  const handleReasonChange = async (selected) => {
    const reasonGuid = selected?.value || "";

    setUpdateForm({
      ...updateForm,
      ticketReason: reasonGuid,
      category: "",
    });

    setCategoryOptions([]);

    if (!reasonGuid) return;

    try {
      const categories = await ticketDropdownApi.getTicketCategory(
        orgId,
        reasonGuid,
      );

      const formattedCategories = categories.map((c) => ({
        value: c.category_guid,
        label: c.ticketcategoryname,
      }));

      setCategoryOptions(formattedCategories);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const handleCategoryChange = (selected) => {
    setUpdateForm({
      ...updateForm,
      category: selected?.value || "",
    });
  };

  // ========== STATUS HANDLERS ==========

  const handleStatusChange = async (selected) => {
    const statusGuid = selected?.value || "";
    const selectedStatus = statusOptions.find((s) => s.value === statusGuid);

    setUpdateForm({
      ...updateForm,
      status: statusGuid,
      subStatus: "",
    });

    const subStatuses = subStatusMap[statusGuid] || [];
    const defaultSubStatus = subStatuses.find((s) => s.isDefault);

    if (defaultSubStatus) {
      setUpdateForm((prev) => ({
        ...prev,
        subStatus: defaultSubStatus.value,
      }));
    }

    // Check if this status requires closure (terminal status)
    const requiresClosure =
      selectedStatus?.requiresClosure ||
      selectedStatus?.isTerminal ||
      selectedStatus?.statusId === "CLOSED";

    if (requiresClosure) {
      // Enable closure mode
      setIsClosureMode(true);

      // Load work log user options specifically for closure if not already loaded
      if (updateForm.projectAssigned) {
        // Show loading indicator
        setLoadingWorkLogUsers(true);
        await buildWorkLogUserOptions();
      } else {
        toast.warning("Please select a project before closing the ticket");
      }

      setClosureForm({
        closureTypeCode: "",
        closureRemarks: "",
        efforts: [{ workedBy: "", effortValue: "", effortUnit: "HOURS" }],
      });
    } else {
      setIsClosureMode(false);
      setClosureForm({
        closureTypeCode: "",
        closureRemarks: "",
        efforts: [{ workedBy: "", effortValue: "", effortUnit: "HOURS" }],
      });
      setIsClosing(false);
    }
  };

  const handleSubStatusChange = (selected) => {
    setUpdateForm({
      ...updateForm,
      subStatus: selected?.value || "",
    });
  };

  // ========== SUBMITTED BY HANDLER ==========

  const handleSubmittedByChange = async (selected) => {
    const submittedByValue = selected?.value || "";

    setUpdateForm({
      ...updateForm,
      submittedBy: submittedByValue,
      projectAssigned: "", // Clear projects when submittedBy changes
      assignedTo: [], // Clear assignedTo
    });

    setAssignedToOptions([]); // Clear existing assignee options
    setWorkLogUserOptions([]); // Clear work log user options

    if (submittedByValue) {
      await fetchProjectsBySubmittedBy(submittedByValue);
    } else {
      setProjectOptionsBySubmitter([]);
    }
  };

  // ========== PROJECT HANDLER ==========

  const handleProjectChange = async (selected) => {
    const ids = selected ? selected.map((o) => o.value) : [];

    setUpdateForm({
      ...updateForm,
      projectAssigned: ids.join(","),
      assignedTo: [], // Clear assignedTo when project changes
    });

    setAssignedToOptions([]); // Clear assignee options while loading
    setWorkLogUserOptions([]); // Clear work log user options while loading

    if (ids.length > 0) {
      await buildAssignedToOptionsWithProjects(ids);

      // If we're in closure mode, also reload work log options
      if (isClosureMode) {
        await buildWorkLogUserOptions(true); // Force refresh
      } else {
        // Still load in background for when closure mode is activated
        buildWorkLogUserOptions(); // Load in background
      }
    } else {
      setAssignedToOptions([]);
      setWorkLogUserOptions([]);
    }
  };

  // ========== WORK LOG MODAL HANDLER ==========

  const handleOpenWorkLogModal = async () => {
    // Refresh work log user options before opening modal
    if (updateForm.projectAssigned) {
      setLoadingWorkLogUsers(true);
      await buildWorkLogUserOptions();
      setLoadingWorkLogUsers(false);
    }
    setShowWorkLogModal(true);
  };

  // ========== CLOSURE HANDLERS ==========

  const handleCloseTicket = async () => {
    if (!closureForm.closureTypeCode) {
      toast.error("Please select closure type");
      return;
    }

    if (
      !closureForm.closureRemarks ||
      closureForm.closureRemarks.trim() === ""
    ) {
      toast.error("Please provide closure remarks");
      return;
    }

    for (const effort of closureForm.efforts) {
      if (!effort.workedBy) {
        toast.error("Please select worked by for all efforts");
        return;
      }
      if (!effort.effortValue || effort.effortValue <= 0) {
        toast.error("Please provide valid effort value for all efforts");
        return;
      }
    }

    setIsClosing(true);

    try {
      const payload = {
        ticketId: ticket.ticketId,
        orgId,
        closureTypeCode: closureForm.closureTypeCode,
        closureRemarks: closureForm.closureRemarks,
        createdBy: String(userDetails.userId),
        efforts: closureForm.efforts.map((e) => ({
          workedBy: e.workedBy,
          effortValue: Number(e.effortValue),
          effortUnit: e.effortUnit,
        })),
      };

      await closeTicket(payload);
      toast.success("Ticket closed successfully");

      setClosureForm({
        closureTypeCode: "",
        closureRemarks: "",
        efforts: [{ workedBy: "", effortValue: "", effortUnit: "HOURS" }],
      });
      setIsClosing(false);

      refreshTickets();
      onClose();
    } catch (error) {
      console.error("Error closing ticket:", error);
      toast.error("Failed to close ticket");
      setIsClosing(false);
    }
  };

  // ========== WORK LOG HANDLER ==========

  const handleAddWorkLog = async () => {
    try {
      if (!workLogForm.workedBy || !workLogForm.effortValue) {
        toast.error("Fill all fields");
        return;
      }

      const payload = {
        ticketId: ticket.ticketId,
        orgId,
        createdBy: String(userDetails.userId),
        changedBy: workLogForm.workedBy,
        effortValue: Number(workLogForm.effortValue),
        effortUnit: workLogForm.effortUnit,
        closureRemarks: workLogForm.remarks,
      };

      await addTicketWorkLog(payload);
      toast.success("Work log added");
      setShowWorkLogModal(false);
      setWorkLogForm({
        workedBy: "",
        effortValue: "",
        effortUnit: "HOURS",
        remarks: "",
      });
      loadWorkLogs();
    } catch (error) {
      console.error("Failed to add work log:", error);
      toast.error("Failed to add work log");
    }
  };

  // ========== UPDATE TICKET HANDLER ==========

  const handleUpdateTicket = async () => {
    try {
      if (!updateForm.dueDate) {
        toast.error("Due Date is required");
        return;
      }

      setLoading(true);

      const payload = {
        ticketId: ticket.ticketId,
        ...updateForm,
        subStatus: updateForm.subStatus,
        assignedTo: updateForm.assignedTo.join(","),
        editedBy: String(userDetails.userId),
      };

      await updateTicket(ticket.ticketId, payload);
      toast.success("Ticket updated successfully");

      refreshTickets();
      onClose();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update ticket");
    } finally {
      setLoading(false);
    }
  };

  // ========== LOAD INITIAL DATA ==========

  useEffect(() => {
    if (orgId) {
      loadTicketStatuses();
      loadSubmittedBy();
      loadDropdownData();
    }
  }, [orgId]);

  // Load type options when modal opens
  useEffect(() => {
    const loadTypeOptionsForEdit = async () => {
      if (!show || !orgId) return;

      try {
        const types = await ticketDropdownApi.getTicketType(orgId);
        const formattedTypes = types.map((t) => ({
          value: t.type_GUID,
          label: t.ticketTypeName,
        }));
        setTypeOptions(formattedTypes);
      } catch (error) {
        console.error("Failed to load types for edit:", error);
      }
    };

    loadTypeOptionsForEdit();
  }, [show, orgId]);

  // Load cascading options (reasons and categories) based on ticket data
  useEffect(() => {
    const loadReasonsAndCategories = async () => {
      if (!show || !ticket) return;

      if (ticket.ticketType) {
        try {
          const reasons = await ticketDropdownApi.getTicketLoggedType(
            orgId,
            ticket.ticketType,
          );

          const formattedReasons = reasons.map((r) => ({
            value: r.ticket_log_guid,
            label: r.ticketdescription,
          }));

          setReasonOptions(formattedReasons);

          if (ticket.ticketReason) {
            const categories = await ticketDropdownApi.getTicketCategory(
              orgId,
              ticket.ticketReason,
            );

            const formattedCategories = categories.map((c) => ({
              value: c.category_guid,
              label: c.ticketcategoryname,
            }));

            setCategoryOptions(formattedCategories);
          }
        } catch (error) {
          console.error("Failed to load reasons/categories:", error);
        }
      }
    };

    loadReasonsAndCategories();
  }, [show, ticket?.ticketType, ticket?.ticketReason, orgId]);

  const loadDropdownData = async () => {
    try {
      if (!orgId) return;

      const types = await ticketDropdownApi.getTicketType(orgId);

      const formattedTypes = types.map((t) => ({
        value: t.type_GUID,
        label: t.ticketTypeName,
      }));

      setTypeOptions(formattedTypes);
    } catch (error) {
      toast.error("Failed to load ticket types");
    }
  };

  // Force re-render when user names are loaded
  useEffect(() => {
    if (
      Object.keys(userNamesCache).length > 0 &&
      workLogsData?.workLogsByUser
    ) {
      setWorkLogsData((prev) => ({ ...prev }));
    }
  }, [userNamesCache]);

  // Reset all states when modal opens with a new ticket
  // Reset all states when modal opens with a new ticket
  useEffect(() => {
    if (show && ticket) {
      setWorkLogsData(null);
      setUserNamesCache({});
      setAssignedToOptions([]);
      setWorkLogUserOptions([]);
      setProjectOptionsBySubmitter([]);
      setIsClosureMode(false); // Reset closure mode
      setClosureForm({
        closureTypeCode: "",
        closureRemarks: "",
        efforts: [{ workedBy: "", effortValue: "", effortUnit: "HOURS" }],
      });
      setIsClosing(false);

      if (ticket.submittedBy) {
        fetchProjectsBySubmittedBy(ticket.submittedBy);
      }

      if (ticket.projectAssigned) {
        const projectIds = ticket.projectAssigned
          .split(",")
          .map((id) => id.trim());
        buildAssignedToOptionsWithProjects(projectIds);
        // Load work log options in background (but don't show closure form yet)
        buildWorkLogUserOptions();
      }

      loadWorkLogs();
    }
  }, [show, ticket?.ticketId]);

  // Reset updateForm when ticket changes
  useEffect(() => {
    if (ticket) {
      setUpdateForm({
        ticketName: ticket.ticketName || "",
        description: ticket.description || "",
        status: ticket.status || "",
        subStatus: ticket.subStatus || "",
        priority: ticket.priority || "Medium",
        assignedTo: ticket.assignedTo
          ? ticket.assignedTo.split(",").map((i) => i.trim())
          : [],
        submittedBy: ticket.submittedBy || "",
        category: ticket.category || "",
        referenceTo: ticket.referenceTo || "",
        dueDate: ticket.dueDate
          ? moment(ticket.dueDate).format("YYYY-MM-DD")
          : "",
        projectAssigned: ticket.projectAssigned || "",
        ticketType: ticket.ticketType || "",
        ticketReason: ticket.ticketReason || "",
      });
    }
  }, [ticket]);

  // Refresh work log options when work log modal opens
  useEffect(() => {
    if (showWorkLogModal && updateForm.projectAssigned) {
      buildWorkLogUserOptions();
    }
  }, [showWorkLogModal, updateForm.projectAssigned]);

  useEffect(() => {
    loadTicketReasons();
    loadClosureTypes();
  }, []);

  const loadTicketReasons = async () => {
    try {
      const res = await getAllTicketLoggedTypes();
      setTicketReasons(res || []);
    } catch {
      toast.error("Failed to load ticket reasons");
    }
  };

  const loadClosureTypes = async () => {
    try {
      const res = await getAllTicketClosureTypes();
      setClosureTypes(res?.data || []);
    } catch {
      toast.error("Failed to load closure types");
    }
  };

  const closureTypeOptions = closureTypes
    .map((c) => ({
      value: c.codefix,
      label: c.description,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  if (!ticket) return null;

  const currentStatus = statusOptions.find(
    (s) => s.value === updateForm.status,
  );
  const currentSubStatuses = subStatusMap[updateForm.status] || [];

  const isStatusTerminal =
    currentStatus?.statusId === "CLOSED" ||
    currentStatus?.requiresClosure ||
    currentStatus?.isTerminal ||
    false;

  // ADD THIS LINE
  const showClosureForm = isStatusTerminal || isClosureMode;

  const getUserDisplayName = (userId) => {
    console.log(`Getting display name for userId: ${userId}`);
    console.log(`Current cache:`, userNamesCache);

    if (!userId) return "N/A";
    if (userNamesCache[userId]) {
      console.log(`Found in cache: ${userNamesCache[userId]}`);
      return userNamesCache[userId];
    }
    console.log(`Not found in cache, returning ID: ${userId}`);
    return userId;
  };
  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="xl"
        dialogClassName="tem-custom-modal-dialog"
        contentClassName="tem-custom-modal-content"
        centered
      >
        <Modal.Header closeButton className="tem-modal-header">
          <Modal.Title className="tem-modal-title">
            Edit Ticket {ticket.ticketId}
          </Modal.Title>
          <div className="tem-modal-footer-buttons">
            <Button
              variant="outline-light"
              size="sm"
              onClick={refreshAllData}
              disabled={refreshing}
              className="tem-btn-refresh"
              title="Refresh Data"
            >
              {refreshing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Refreshing...
                </>
              ) : (
                <IoMdRefreshCircle style={{ fontSize: "2rem" }} />
              )}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            {isStatusTerminal ? (
              <Button
                variant="danger"
                onClick={handleCloseTicket}
                disabled={loading || isClosing}
              >
                {isClosing ? "Closing..." : "Close Ticket"}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleUpdateTicket}
                disabled={loading}
              >
                {loading ? "Updating..." : "Update Ticket"}
              </Button>
            )}
          </div>
        </Modal.Header>

        <Modal.Body className="tem-modal-body">
          <Row>
            {/* LEFT COLUMN - Edit Form */}
            <Col md={6} className="tem-left-column">
              <div>
                <h6 className="tem-section-header tem-section-header-primary">
                  📋 Ticket Information
                </h6>

                {/* Loading Indicators */}
                {(loadingAssignedTo || loadingStatuses) && (
                  <div className="tem-loading-alert">
                    <Spinner
                      animation="border"
                      size="sm"
                      className="tem-loading-spinner"
                    />
                    <span>
                      {loadingStatuses
                        ? "Loading status options..."
                        : "Loading assignee options..."}
                    </span>
                  </div>
                )}

                {/* Ticket Name */}
                <Form.Group className="tem-form-group">
                  <Form.Label className="tem-form-label tem-form-label-required">
                    Ticket Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    className="tem-form-control"
                    value={updateForm.ticketName}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        ticketName: e.target.value,
                      })
                    }
                    placeholder="Enter ticket name..."
                  />
                </Form.Group>

                {/* Submitted By */}
                <Form.Group className="tem-form-group">
                  <Form.Label className="tem-form-label tem-form-label-required">
                    Submitted By
                  </Form.Label>
                  {loadingSubmittedBy ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <Select
                      className="tem-select"
                      classNamePrefix="tem-select"
                      options={submittedByOptions}
                      value={
                        submittedByOptions.find(
                          (opt) => opt.value === updateForm.submittedBy,
                        ) || null
                      }
                      onChange={handleSubmittedByChange}
                      placeholder="Select submitter..."
                      isClearable
                    />
                  )}
                </Form.Group>

                {/* Ticket Type, Reason, Category */}
                <Row>
                  <Col md={12} className="mb-3">
                    <Form.Label className="tem-form-label">
                      Ticket Type
                    </Form.Label>
                    <Select
                      className="tem-select"
                      classNamePrefix="tem-select"
                      options={typeOptions}
                      value={typeOptions.find(
                        (opt) => opt.value === updateForm.ticketType,
                      )}
                      onChange={handleTypeChange}
                      placeholder="Select Type"
                    />
                  </Col>
                  <Col md={12} className="mb-3">
                    <Form.Label className="tem-form-label">
                      Ticket Reason
                    </Form.Label>
                    <Select
                      className="tem-select"
                      classNamePrefix="tem-select"
                      options={reasonOptions}
                      value={reasonOptions.find(
                        (opt) => opt.value === updateForm.ticketReason,
                      )}
                      onChange={handleReasonChange}
                      placeholder="Select Reason"
                      isDisabled={!updateForm.ticketType}
                    />
                  </Col>
                  <Col md={12} className="mb-3">
                    <Form.Label className="tem-form-label">Category</Form.Label>
                    <Select
                      className="tem-select"
                      classNamePrefix="tem-select"
                      options={categoryOptions}
                      value={categoryOptions.find(
                        (opt) => opt.value === updateForm.category,
                      )}
                      onChange={handleCategoryChange}
                      placeholder="Select Category"
                      isDisabled={!updateForm.ticketReason}
                    />
                  </Col>
                </Row>

                {/* Priority and Due Date */}
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label className="tem-form-label">Priority</Form.Label>
                    <Form.Select
                      className="tem-form-select"
                      value={updateForm.priority}
                      onChange={(e) =>
                        setUpdateForm({
                          ...updateForm,
                          priority: e.target.value,
                        })
                      }
                    >
                      <option value="Low">🔵 Low</option>
                      <option value="Normal">🟢 Normal</option>
                      <option value="Medium">🟡 Medium</option>
                      <option value="High">🟠 High</option>
                      <option value="Critical">🔴 Critical</option>
                    </Form.Select>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label className="tem-form-label tem-form-label-required">
                      Due Date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      className="tem-form-control"
                      required
                      value={updateForm.dueDate}
                      onChange={(e) =>
                        setUpdateForm({
                          ...updateForm,
                          dueDate: e.target.value,
                        })
                      }
                    />
                  </Col>
                </Row>

                {/* Project Assigned */}
                <Form.Group className="tem-form-group">
                  <Form.Label className="tem-form-label tem-form-label-required">
                    Project Assigned
                  </Form.Label>
                  {loadingProjects ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">Loading projects...</span>
                    </div>
                  ) : isProjectReadOnly ? (
                    <Form.Control
                      type="text"
                      className="tem-form-control bg-light"
                      value={
                        currentProject
                          ? `${currentProject.prTitle} (${currentProject.projectId})`
                          : updateForm.projectAssigned
                      }
                      disabled
                    />
                  ) : (
                    <>
                      <Select
                        className="tem-select"
                        classNamePrefix="tem-select"
                        isMulti
                        options={projectOptionsBySubmitter}
                        value={projectOptionsBySubmitter.filter((opt) =>
                          updateForm.projectAssigned
                            ?.split(",")
                            .includes(opt.value),
                        )}
                        onChange={handleProjectChange}
                        placeholder={
                          updateForm.submittedBy
                            ? "Select projects..."
                            : "⚠️ Please select Submitted By first"
                        }
                        isDisabled={!updateForm.submittedBy || loadingProjects}
                        noOptionsMessage={() =>
                          !updateForm.submittedBy
                            ? "Please select Submitted By first"
                            : "No projects available for this submitter"
                        }
                      />
                      {updateForm.submittedBy &&
                        projectOptionsBySubmitter.length > 0 && (
                          <div
                            className={`tem-project-count-badge ${
                              updateForm.projectAssigned
                                ? "tem-project-count-badge-primary"
                                : "tem-project-count-badge-secondary"
                            }`}
                          >
                            📁 {getProjectCountText()}
                          </div>
                        )}
                    </>
                  )}
                </Form.Group>

                {/* Assigned To */}
                <Form.Group className="tem-form-group">
                  <Form.Label className="tem-form-label">
                    Assigned To
                  </Form.Label>
                  {loadingAssignedTo ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <Select
                      className="tem-select"
                      classNamePrefix="tem-select"
                      isMulti
                      options={assignedToOptions}
                      value={assignedToOptions.filter((opt) =>
                        updateForm.assignedTo.includes(opt.value),
                      )}
                      onChange={(selected) =>
                        setUpdateForm({
                          ...updateForm,
                          assignedTo: selected
                            ? selected.map((o) => o.value)
                            : [],
                        })
                      }
                      placeholder="Select assignees..."
                    />
                  )}
                  <small className="tem-assignee-count">
                    👥 {assignedToOptions.length} assignee(s) available
                  </small>
                </Form.Group>

                {/* Status and Sub Status */}
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label className="tem-form-label tem-form-label-required">
                      Status
                    </Form.Label>
                    <Select
                      className="tem-select"
                      classNamePrefix="tem-select"
                      options={statusOptions}
                      value={statusOptions.find(
                        (opt) => opt.value === updateForm.status,
                      )}
                      onChange={handleStatusChange}
                      placeholder="Select Status"
                      isLoading={loadingStatuses}
                      isDisabled={loadingStatuses}
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label className="tem-form-label">
                      Sub Status
                    </Form.Label>
                    <Select
                      className="tem-select"
                      classNamePrefix="tem-select"
                      options={currentSubStatuses}
                      value={currentSubStatuses.find(
                        (opt) => opt.value === updateForm.subStatus,
                      )}
                      onChange={handleSubStatusChange}
                      placeholder="Select Sub Status"
                      isDisabled={!updateForm.status || loadingStatuses}
                    />
                  </Col>
                </Row>

                {/* Description */}
                <Form.Group className="tem-form-group">
                  <Form.Label className="tem-form-label">
                    Description
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    className="tem-form-control"
                    value={updateForm.description}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter ticket description..."
                  />
                </Form.Group>

                {/* Inline Closure Form - Shown only for terminal statuses */}
                {showClosureForm && (
                  <div className="tem-closure-section">
                    <div className="tem-closure-header">
                      <h6 className="tem-closure-title">
                        Close Ticket Details
                      </h6>
                      {isClosureMode && updateForm.projectAssigned && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="tem-refresh-team-btn"
                          onClick={() => buildWorkLogUserOptions(true)}
                          disabled={loadingWorkLogUsers}
                        >
                          <IoMdRefreshCircle style={{ marginRight: "5px" }} />
                          Refresh Team Members
                        </Button>
                      )}
                    </div>

                    <Form.Group className="tem-form-group">
                      <Form.Label className="tem-form-label tem-form-label-required">
                        Closure Type
                      </Form.Label>
                      <Select
                        className="tem-select"
                        classNamePrefix="tem-select"
                        options={closureTypeOptions}
                        value={closureTypeOptions.find(
                          (o) => o.value === closureForm.closureTypeCode,
                        )}
                        onChange={(opt) =>
                          setClosureForm({
                            ...closureForm,
                            closureTypeCode: opt?.value,
                          })
                        }
                        placeholder="Select closure type"
                      />
                    </Form.Group>

                    <Form.Group className="tem-form-group">
                      <Form.Label className="tem-form-label tem-form-label-required">
                        Closure Remarks
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        className="tem-form-control"
                        value={closureForm.closureRemarks}
                        onChange={(e) =>
                          setClosureForm({
                            ...closureForm,
                            closureRemarks: e.target.value,
                          })
                        }
                        placeholder="Provide closure remarks..."
                      />
                    </Form.Group>

                    <hr className="tem-divider" />
                    <h6 className="tem-form-label mb-3">
                      ⏱️ Efforts (Work Log)
                    </h6>

                    {/* Check if project is selected */}
                    {!updateForm.projectAssigned && (
                      <div className="tem-warning-alert">
                        ⚠️ Please select a project before adding closure
                        efforts.
                      </div>
                    )}

                    {/* Show loading state */}
                    {loadingWorkLogUsers && updateForm.projectAssigned && (
                      <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted">
                          Loading team members for selected project(s)...
                        </p>
                      </div>
                    )}

                    {/* Show no data message */}
                    {!loadingWorkLogUsers &&
                      updateForm.projectAssigned &&
                      workLogUserOptions.length === 0 && (
                        <div className="tem-info-alert">
                          ℹ️ No team members found for the selected project(s).
                          Please ensure:
                          <ul className="mt-2 mb-0">
                            <li>Team members are assigned to the project</li>
                            <li>
                              Customers/Partners are associated with the project
                            </li>
                            <li>You have the correct permissions</li>
                          </ul>
                        </div>
                      )}

                    {/* Show the efforts form */}
                    {updateForm.projectAssigned && !loadingWorkLogUsers && (
                      <>
                        {closureForm.efforts.map((effort, index) => (
                          <Row key={index} className="tem-effort-row">
                            <Col md={12} className="mb-3">
                              <Form.Label className="tem-form-label tem-form-label-required">
                                Worked By
                              </Form.Label>
                              <Select
                                className="tem-select"
                                classNamePrefix="tem-select"
                                options={workLogUserOptions}
                                value={workLogUserOptions.find(
                                  (o) => o.value === effort.workedBy,
                                )}
                                onChange={(opt) => {
                                  const updated = [...closureForm.efforts];
                                  updated[index].workedBy = opt?.value || "";
                                  setClosureForm({
                                    ...closureForm,
                                    efforts: updated,
                                  });
                                }}
                                placeholder={
                                  workLogUserOptions.length === 0
                                    ? "No team members available"
                                    : "Select team member..."
                                }
                                isDisabled={workLogUserOptions.length === 0}
                                noOptionsMessage={() =>
                                  workLogUserOptions.length === 0
                                    ? "No team members found for this project"
                                    : "No options"
                                }
                              />
                            </Col>
                            <Col md={6} className="mb-3">
                              <Form.Label className="tem-form-label tem-form-label-required">
                                Effort
                              </Form.Label>
                              <Form.Control
                                type="number"
                                className="tem-form-control"
                                value={effort.effortValue}
                                onChange={(e) => {
                                  const updated = [...closureForm.efforts];
                                  updated[index].effortValue = e.target.value;
                                  setClosureForm({
                                    ...closureForm,
                                    efforts: updated,
                                  });
                                }}
                                placeholder="Enter hours"
                                min="0"
                                step="0.5"
                              />
                            </Col>
                            <Col md={4} className="mb-3">
                              <Form.Label className="tem-form-label">
                                Unit
                              </Form.Label>
                              <Form.Select
                                className="tem-form-select"
                                value={effort.effortUnit}
                                onChange={(e) => {
                                  const updated = [...closureForm.efforts];
                                  updated[index].effortUnit = e.target.value;
                                  setClosureForm({
                                    ...closureForm,
                                    efforts: updated,
                                  });
                                }}
                              >
                                <option value="HOURS">Hours</option>
                                <option value="DAYS">Days</option>
                              </Form.Select>
                            </Col>
                            <Col md={2} className="d-flex align-items-end mb-3">
                              <Button
                                variant="danger"
                                size="sm"
                                className="w-100"
                                onClick={() => {
                                  const updated = closureForm.efforts.filter(
                                    (_, i) => i !== index,
                                  );
                                  setClosureForm({
                                    ...closureForm,
                                    efforts: updated,
                                  });
                                }}
                                disabled={closureForm.efforts.length === 1}
                              >
                                ✕ Remove
                              </Button>
                            </Col>
                          </Row>
                        ))}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="tem-add-effort-btn"
                          onClick={() =>
                            setClosureForm({
                              ...closureForm,
                              efforts: [
                                ...closureForm.efforts,
                                {
                                  workedBy: "",
                                  effortValue: "",
                                  effortUnit: "HOURS",
                                },
                              ],
                            })
                          }
                          disabled={workLogUserOptions.length === 0}
                        >
                          + Add Another Effort
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Col>

            {/* RIGHT COLUMN - Work Log History */}
            <Col md={6} className="tem-right-column">
              <div>
                <div className="tem-worklog-header">
                  <h6 className="tem-worklog-title">📊 Work Log History</h6>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="tem-add-worklog-btn"
                    onClick={handleOpenWorkLogModal}
                  >
                    + Add Work Log
                  </Button>
                </div>

                {loadingWorkLogs || refreshing ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">
                      {refreshing
                        ? "Refreshing data..."
                        : "Loading work logs..."}
                    </span>
                  </div>
                ) : workLogsData?.workLogsByUser &&
                  workLogsData.workLogsByUser.length > 0 ? (
                  <>
                    <div className="tem-worklog-summary">
                      📈 Total Work Logged:{" "}
                      <strong>
                        {workLogsData.summary?.totalWorkLogHours || 0} hours
                      </strong>{" "}
                      | 📝 Total Entries:{" "}
                      <strong>
                        {workLogsData.summary?.totalWorkLogs || 0}
                      </strong>
                    </div>

                    <div className="tem-worklog-scroll-container">
                      {workLogsData.workLogsByUser.map((userWork) => (
                        <div
                          key={userWork.userId}
                          className="tem-worklog-user-card"
                        >
                          <div className="tem-worklog-user-header">
                            <div className="tem-worklog-user-info">
                              <Badge
                                bg="info"
                                className="tem-worklog-badge-count"
                              >
                                {userWork.logCount} entry(s)
                              </Badge>
                              <strong className="tem-worklog-user-name">
                                {loadingUserNames ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  getUserDisplayName(userWork.userId)
                                )}
                              </strong>
                            </div>
                            <Badge
                              bg="primary"
                              className="tem-worklog-total-badge"
                            >
                              Total: {userWork.totalHours} hours
                            </Badge>
                          </div>

                          <Table
                            responsive
                            size="sm"
                            bordered
                            hover
                            className="tem-worklog-table"
                          >
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Hours</th>
                                <th>Unit</th>
                                <th>Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userWork.logs.map((log, idx) => (
                                <tr key={log.ticketLogGuid || idx}>
                                  <td>
                                    {log.changedDate &&
                                    log.changedDate !== "0001-01-01T00:00:00"
                                      ? moment(log.changedDate).format(
                                          "DD-MMM-YYYY",
                                        )
                                      : moment(log.createdDate).format(
                                          "DD-MMM-YYYY",
                                        )}
                                  </td>
                                  <td>{log.effortValue}</td>
                                  <td>{log.effortUnit || "HOURS"}</td>
                                  <td className="tem-worklog-remarks-cell">
                                    {log.closureRemarks || "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="tem-empty-state">
                    <i>No work logs found for this ticket</i>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      {/* Work Log Modal */}
      <Modal
        show={showWorkLogModal}
        onHide={() => setShowWorkLogModal(false)}
        centered
      >
        <Modal.Header closeButton className="tem-modal-header">
          <Modal.Title className="tem-modal-title">⏱️ Add Work Log</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tem-modal-body">
          <Form>
            <Form.Group className="tem-form-group">
              <Form.Label className="tem-form-label tem-form-label-required">
                Worked By
              </Form.Label>
              {loadingWorkLogUsers ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading users...</span>
                </div>
              ) : (
                <Select
                  className="tem-select"
                  classNamePrefix="tem-select"
                  options={workLogUserOptions}
                  value={workLogUserOptions.find(
                    (o) => o.value === workLogForm.workedBy,
                  )}
                  onChange={(opt) =>
                    setWorkLogForm({
                      ...workLogForm,
                      workedBy: opt?.value,
                    })
                  }
                  placeholder={
                    updateForm.projectAssigned
                      ? "Select user..."
                      : "⚠️ Please select a project first"
                  }
                  isDisabled={
                    !updateForm.projectAssigned || loadingWorkLogUsers
                  }
                  noOptionsMessage={() =>
                    !updateForm.projectAssigned
                      ? "Please select a project first"
                      : "No users available for selected project(s)"
                  }
                />
              )}
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="tem-form-group">
                  <Form.Label className="tem-form-label tem-form-label-required">
                    Effort (Hours)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    className="tem-form-control"
                    value={workLogForm.effortValue}
                    onChange={(e) =>
                      setWorkLogForm({
                        ...workLogForm,
                        effortValue: e.target.value,
                      })
                    }
                    placeholder="Enter effort hours"
                    min="0"
                    step="0.5"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="tem-form-group">
                  <Form.Label className="tem-form-label">Unit</Form.Label>
                  <Form.Select
                    className="tem-form-select"
                    value={workLogForm.effortUnit}
                    onChange={(e) =>
                      setWorkLogForm({
                        ...workLogForm,
                        effortUnit: e.target.value,
                      })
                    }
                  >
                    <option value="HOURS">Hours</option>
                    <option value="DAYS">Days</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="tem-form-group">
              <Form.Label className="tem-form-label">Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                className="tem-form-control"
                value={workLogForm.remarks}
                onChange={(e) =>
                  setWorkLogForm({
                    ...workLogForm,
                    remarks: e.target.value,
                  })
                }
                placeholder="Enter work log remarks..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowWorkLogModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddWorkLog}
            disabled={
              !updateForm.projectAssigned ||
              !workLogForm.workedBy ||
              !workLogForm.effortValue
            }
          >
            💾 Save Work Log
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TicketEditModal;
